import { SCORE_CRITERIA_MAX } from '@parcel/filters';
import { Badge, Button, Skeleton, toast } from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { Building2, FolderKanban, Sparkles, UserRound } from 'lucide-react';
import * as React from 'react';
import { apiErrorMessage } from '../../lib/api.js';
import { useWorkspaceFilterOptions } from '../../lib/filter-options.js';
import { useAskFilters } from '../../lib/filters-ask.js';
import { useOpenSpecChat, useSpecChatAvailable } from '../../lib/open-spec-chat.js';
import {
  ENTITY_BY_DATASET,
  type Score,
  type ScoreDataset,
  TEMPLATE_KEY_BY_DATASET,
  isVersionConflict,
  usePreviewScore,
  useScore,
  useScores,
  useUpdateScore,
} from '../../lib/scores.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { useWhereOptions } from '../../lib/where-options.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { SettingsShell } from '../settings/settings-shell.js';
import { criteriaFromAsk } from './ask-criteria.js';
import { CriteriaCatalog } from './criteria-catalog.js';
import {
  type CardKind,
  type EditorState,
  addCriterion,
  cardOf,
  criterionIssues,
  editorFor,
  groupCriteria,
  newCriterionFor,
  previewableCriteria,
  removeCriterion,
  updateCriterion,
} from './criteria-state.js';
import { stripBuilderKeys } from './criterion-builder.js';
import { CriterionCard } from './criterion-card.js';
import { ScoreDistribution } from './score-distribution.js';
import { SeedDialog } from './seed-dialog.js';

const TITLE: Record<ScoreDataset, string> = {
  accounts: 'Accounts',
  projects: 'Projects',
  contacts: 'Contacts',
};
const BLURB: Record<ScoreDataset, string> = {
  accounts: 'Ranks every account by how well it fits your ideal customer.',
  projects: 'Ranks every project by how well it fits the work you pursue.',
  contacts: 'Ranks every contact by persona and by the account they work at.',
};
const ICON = { accounts: Building2, projects: FolderKanban, contacts: UserRound } as const;
const PREVIEW_DEBOUNCE_MS = 400;

export function ScoreSettingsPage({ dataset }: { dataset: ScoreDataset }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspace = workspaceQuery.data;
  const workspaceId = workspace?.id;
  const entity = ENTITY_BY_DATASET[dataset];
  const key = TEMPLATE_KEY_BY_DATASET[dataset];
  const scores = useScores(workspaceId);
  const score = useScore(workspaceId, key).data;
  const update = useUpdateScore(workspaceId, key);
  const preview = usePreviewScore(workspaceId, key);
  const options = useWhereOptions(workspaceId, entity);
  const { mutateAsync: askFilters, isPending: asking } = useAskFilters();
  const openSpecChat = useOpenSpecChat();
  const specAvailable = useSpecChatAvailable();
  const cityOptions = useWorkspaceFilterOptions(workspaceId);
  const cities = cityOptions.data?.cities;
  const cityNames = React.useMemo(() => cities?.map((city) => city.value), [cities]);
  const canEdit = workspace?.role === 'owner' || workspace?.role === 'admin';
  const [seeding, setSeeding] = React.useState(false);

  const [state, setState] = React.useState<EditorState | null>(null);
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const [loaded, setLoaded] = React.useState<{ id: string; version: number } | null>(null);
  const load = React.useCallback(
    (next: Score) => {
      setState(editorFor(ENTITY_BY_DATASET[dataset], next.criteria));
      setLoaded({ id: next.id, version: next.version });
    },
    [dataset],
  );
  // Another score always replaces the editor; a fresh read of this one replaces it only while clean.
  React.useEffect(() => {
    if (!score) return;
    if (
      state === null ||
      loaded?.id !== score.id ||
      (!state.dirty && loaded.version !== score.version)
    ) {
      load(score);
    }
  }, [score, state, loaded, load]);

  const issues = React.useMemo(
    () => criterionIssues(entity, state?.criteria ?? []),
    [entity, state?.criteria],
  );
  const runnable = React.useMemo(
    () => previewableCriteria(entity, state?.criteria ?? []),
    [entity, state?.criteria],
  );
  const runPreview = preview.mutate;
  const runnableKey = JSON.stringify(runnable);
  React.useEffect(() => {
    if (!workspaceId || runnable.length === 0) return;
    const handle = globalThis.setTimeout(
      () => runPreview({ criteria: JSON.parse(runnableKey) }),
      PREVIEW_DEBOUNCE_MS,
    );
    return () => globalThis.clearTimeout(handle);
  }, [workspaceId, runnableKey, runnable.length, runPreview]);

  const reload = () => {
    if (!score) return;
    void scores.refetch().then((result) => {
      const fresh = result.data?.find((s) => s.key === key);
      if (fresh) load(fresh);
      update.reset();
    });
  };

  if (!state || !score || loaded?.id !== score.id) {
    return (
      <SettingsShell wide>
        <h2 className="mb-6 text-2xl font-semibold tracking-tight">{TITLE[dataset]} scoring</h2>
        {scores.isError ? (
          <p className="text-sm text-muted-foreground">Couldn't load scoring.</p>
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </SettingsShell>
    );
  }

  const blocked = issues.size > 0;
  // A newer version under unsaved edits: say so, rather than overwrite either side.
  const updatedElsewhere =
    state.dirty &&
    !update.isPending &&
    !isVersionConflict(update.error) &&
    loaded?.id === score.id &&
    score.version > (loaded?.version ?? 0);
  const Icon = ICON[dataset];
  const save = () => {
    const sent = JSON.stringify(state.criteria);
    update.mutate(
      {
        expected_version: loaded?.version ?? score.version,
        // The three template scores keep their bucket names; the UI never renames them.
        bucket_labels: score.buckets.map((bucket) => bucket.label),
        // A blank name is left out: the criterion reads by its rules.
        criteria: state.criteria.map(({ label, where, ...rest }) => ({
          ...rest,
          ...(label?.trim() ? { label } : {}),
          where: stripBuilderKeys(where) as typeof where,
        })),
      },
      {
        // An edit made while the save ran (a card, an Ask) stays, on top of the saved version.
        onSuccess: (saved) => {
          setLoaded({ id: saved.id, version: saved.version });
          setState((current) =>
            current && JSON.stringify(current.criteria) !== sent
              ? current
              : editorFor(entity, saved.criteria),
          );
        },
      },
    );
  };
  const addRow = (card: CardKind) =>
    setState(
      addCriterion(
        state,
        newCriterionFor(entity, card.type === 'custom' ? { type: 'custom' } : card),
        { custom: card.type === 'custom' },
      ),
    );
  // Ask: the question becomes criteria at Important; what it could not read is named in a notice.
  const ask = async (question: string) => {
    if (!workspaceId) return;
    try {
      const result = await askFilters({ workspaceId, entity, query: question });
      const found = criteriaFromAsk(entity, result.where);
      // Room is counted on the editor as it is now, not as it was when the question went out.
      const room = SCORE_CRITERIA_MAX - (stateRef.current?.criteria.length ?? 0);
      setState((current) => {
        const base = current ?? state;
        const fit = found.slice(0, Math.max(0, SCORE_CRITERIA_MAX - base.criteria.length));
        // An "any of" or "not" answer is a custom condition and stays one while edited.
        return fit.reduce(
          (next, c) =>
            addCriterion(next, c, {
              custom: cardOf(entity, { ...c, id: '_' }).type === 'custom',
            }),
          base,
        );
      });
      if (found.length > room) {
        toast.warning(
          `A score holds ${SCORE_CRITERIA_MAX} criteria; ${found.length - Math.max(0, room)} from Ask did not fit.`,
        );
      }
      if (result.unmapped.length > 0) {
        toast.warning(`Couldn’t translate ${result.unmapped.map((u) => `“${u}”`).join(', ')}`);
      } else if (found.length === 0) {
        toast.warning('Ask found no criteria in that question.');
      }
    } catch {
      toast.error('Couldn’t translate that question.');
    }
  };
  // Seeding replaced this score's criteria, unsaved edits included.
  const seeded = (next: Score[]) => {
    const fresh = next.find((candidate) => candidate.key === key);
    if (fresh) load(fresh);
    update.reset();
  };
  const groups = groupCriteria(entity, state.criteria, state.custom);
  const matches = new Map((preview.data?.criteria ?? []).map((c) => [c.id, c.matches]));
  // Counts belong to the criteria they were run for; an edit hides them until its own preview answers.
  const countsFresh =
    !preview.isPending &&
    preview.variables !== undefined &&
    JSON.stringify(preview.variables.criteria) === runnableKey;
  const buckets = score.buckets;

  return (
    <SettingsShell fill>
      <header className="mb-6 flex shrink-0 flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold tracking-tight">{score.name}</h2>
            <Badge variant="secondary" className="gap-1 font-normal">
              <Icon className="size-3.5" aria-hidden />
              {TITLE[dataset]}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{BLURB[dataset]}</p>
        </div>
        {canEdit ? (
          <div className="flex items-center gap-2">
            {specAvailable ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() =>
                  void openSpecChat({
                    starter: `Help me set up my ${score.name}. Look at my ${TITLE[dataset].toLowerCase()} and what my workspace knows, then propose criteria I can approve.`,
                    title: `Set up ${score.name}`,
                    beside: true,
                  }).catch(() => toast.error('Spec could not open that chat.'))
                }
              >
                <Sparkles className="size-4" aria-hidden />
                Set up with Spec
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              disabled={!state.dirty || update.isPending}
              onClick={() => {
                load(score);
                update.reset();
              }}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={!state.dirty || blocked || update.isPending} onClick={save}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        ) : null}
      </header>
      {canEdit ? (
        <SeedDialog
          workspaceId={workspaceId}
          cities={cityNames}
          scores={scores.data}
          failed={cityOptions.isError || scores.isError}
          onRetry={() => {
            void cityOptions.refetch();
            void scores.refetch();
          }}
          open={seeding}
          onOpenChange={setSeeding}
          onSeeded={seeded}
        />
      ) : null}
      {updatedElsewhere ? (
        <p className="mb-4 text-sm text-destructive">
          This score was updated while you were editing.{' '}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => {
              load(score);
              update.reset();
            }}
          >
            Reload
          </button>
        </p>
      ) : null}
      {update.error ? (
        isVersionConflict(update.error) ? (
          <p className="mb-4 text-sm text-destructive">
            Someone changed this score; reload.{' '}
            <button type="button" className="underline underline-offset-2" onClick={reload}>
              Reload
            </button>
          </p>
        ) : (
          <p className="mb-4 text-sm text-destructive">
            {apiErrorMessage(update.error, "Couldn't save the score.")}
          </p>
        )
      ) : null}
      {!canEdit ? (
        <p className="mb-4 text-sm text-muted-foreground">
          Only owners and admins change scoring. You can read the criteria and the distribution.
        </p>
      ) : null}

      <section className="mb-8 shrink-0 space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-medium">Score distribution</h3>
          <span className="text-xs text-muted-foreground">
            {runnable.length === 0
              ? 'Add a criterion to see a preview.'
              : 'With these criteria. Nothing changes until you save.'}
          </span>
        </div>
        <ScoreDistribution
          entity={entity}
          preview={runnable.length ? preview.data : undefined}
          loading={preview.isPending}
          empty={runnable.length === 0}
          buckets={buckets}
        />
        {preview.error ? (
          <p className="text-xs text-destructive">
            {apiErrorMessage(preview.error, "Couldn't preview these criteria.")}
          </p>
        ) : null}
      </section>

      <div className="grid gap-8 lg:min-h-0 lg:flex-1 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="lg:flex lg:min-h-0 lg:flex-col lg:border-r lg:pr-6">
          <CriteriaCatalog
            entity={entity}
            disabled={!canEdit || state.criteria.length >= SCORE_CRITERIA_MAX}
            onPick={(pick) =>
              setState(
                addCriterion(state, newCriterionFor(entity, pick), {
                  custom: pick.type === 'custom',
                }),
              )
            }
            onAsk={canEdit ? (question) => void ask(question) : undefined}
            asking={asking}
          />
        </div>
        <section className="flex min-w-0 flex-col gap-3 lg:min-h-0">
          <h3 className="shrink-0 text-sm font-medium">Score criteria</h3>
          <div className="space-y-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pb-8">
            {groups.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No criteria yet, so nothing is scored. Add some from the list
                {canEdit ? (
                  <>
                    , or{' '}
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      disabled={update.isPending}
                      onClick={() => setSeeding(true)}
                    >
                      start from a few questions
                    </button>
                  </>
                ) : null}
                .
              </div>
            ) : (
              groups.map((group) => (
                <CriterionCard
                  key={group.key}
                  entity={entity}
                  group={group}
                  issues={issues}
                  matches={countsFresh ? matches : undefined}
                  disabled={!canEdit}
                  options={options}
                  onChange={(id, patch) => setState(updateCriterion(state, id, patch))}
                  onRemove={(ids) =>
                    setState(ids.reduce((next, id) => removeCriterion(next, id), state))
                  }
                  canAddRow={state.criteria.length < SCORE_CRITERIA_MAX}
                  onAddRow={() => addRow(group.card)}
                />
              ))
            )}
          </div>
        </section>
      </div>
    </SettingsShell>
  );
}
